import {
  GLOBAL_VARIABLE_TAG_KEY,
  TAG_TYPE_KEY,
  TREE_GLOBAL_VARIABLE_TAG_KEY,
} from '@/common/define/widget.define';
import { FilterUUID, WidgetUUID } from '@/common/utils';
import type { GlobalVariable } from '@/dashboard/features/globalVariables/domain/globalVariable.types';
import { useStandardVariablesStore } from '@/dashboard/stores/global-variables/standard-variables';
import { useTreeVariablesStore } from '@/dashboard/stores/global-variables/tree-variables';
import { useGlobalTimePeriodStore } from '@/dashboard/stores/global-time-period';
import { isQueryManagerStatId } from '@/dashboard/features/widgets/common/domain/reference';
import { WidgetCommonArgument } from '@/dashboard/features/widgets/common/constants/define';
import { WidgetChartType, WidgetTarget } from '@/dashboard/shared/types/widgets.types';
import { storeToRefs } from 'pinia';
import { ComputedRef, MaybeRefOrGetter, toValue } from 'vue';
import { isNil } from 'lodash-es';
import { useStatInfoStore } from '@/common/stores/stat-info';
import { useWidgetClickStore } from '@/dashboard/stores/widget-click';
import { useStatDetailStore } from '@/dashboard/stores/stat-detail';
import { useDashboardLegendStore } from '@/dashboard/stores/dashboard-legend';
import { useDashboardTargetsStore } from '@/dashboard/stores/dashboard-targets';
import {
  CommonChartDataIndex,
  CommonStatId,
  ReferenceInfo,
  ReferenceVariable,
} from '@/dashboard/features/widgetSettings/editModeWidgetSettings/components/common/referenceSettingOption/referenceSettingOption.setup';
import {
  DataFieldName,
  PeriodTimeType,
  WidgetVariable,
} from '@/dashboard/features/widgetSettings/editModeWidgetSettings/components/common/referenceSettingOption/variableListWindow/variable.types';
import { useWidgetArgumentList } from '@/dashboard/features/widgetSettings/editModeWidgetSettings/composables/useWidgetSettingsWindow';
import { useRequestTags } from '@/dashboard/features/widgetsAPI/composables/useRequestTags';
import type { UserTagRequest } from '@/api-client/data/model/user-tag-request';
import type { TargetKeyNameItem } from '@/api-client/data/model/target-key-name-item';
import {
  ArgParam,
  decodeGlobalVariableArgParam,
  encodeArgParam,
  encodeGlobalVariableArgParam,
} from '../../domain/referenceParam';

/**
 * 범례 필터 활성 여부는 `mode !== 'noFilter'`로, 빈 결과 여부는 `mode === 'empty'`로 판별한다.
 * - noFilter : selectedLegends === [] (범례 필터 없음, 전체 선택)
 * - empty    : selectedLegends === null (범례 전체 해제) 또는 매칭 타겟 없음
 * - filtered : 매칭된 타겟 목록
 */
type LegendFilterResult =
  | { mode: 'noFilter' }
  | { mode: 'empty' }
  | { mode: 'filtered'; targets: TargetKeyNameItem[] };

export const useWidgetArgParam = (
  _referenceInfo: ComputedRef<ReferenceInfo>,
  options?: {
    applyDefaultValues?: MaybeRefOrGetter<boolean | undefined>;
  },
) => {
  const { getClickedWidget } = useWidgetClickStore();

  const { getArgumentList } = useWidgetArgumentList();

  const { globalTimePeriod } = storeToRefs(useGlobalTimePeriodStore());
  const { getRequestUserTags } = useRequestTags();
  const globalFilterStore = useStandardVariablesStore();
  const { standardVariables } = storeToRefs(globalFilterStore);
  const dashboardLegendStore = useDashboardLegendStore();
  const dashboardTargetsStore = useDashboardTargetsStore();

  const resolveLegendFilter = (widgetId: WidgetUUID): LegendFilterResult => {
    const { selectedLegends } = dashboardLegendStore;
    if (selectedLegends === null) return { mode: 'empty' };
    if (!selectedLegends.length) return { mode: 'noFilter' };
    const targets =
      dashboardTargetsStore.allDashboardTargetsColumns
        ?.filter((widget) => widget.widgetUUID === widgetId)
        .flatMap((widget) =>
          widget.targets.filter(({ id }) =>
            selectedLegends.some(({ targetId }) => targetId === id),
          ),
        ) ?? [];
    return targets.length ? { mode: 'filtered', targets } : { mode: 'empty' };
  };

  const getArgParamValue = (
    info: {
      widgetId: WidgetUUID;
      filterId: FilterUUID | DataFieldName;
      timeType?: PeriodTimeType;
    }[],
    { useNullString = false }: { useNullString?: boolean } = {},
  ) => {
    // NOTE: 클릭한 위젯의 데이터가 있는 경우, 해당 데이터를 반환합니다.
    const { data, filterType } = getClickedWidget(info ?? []) || {};
    if (useNullString && data === '' && filterType !== 'text') {
      return '';
    }
    if (!isNil(data)) {
      return encodeArgParam(data);
    }
    return '';
  };

  const extractArgParamValue = (
    widgetVariable: WidgetVariable,
    {
      useNullString = false,
      legendFilter,
    }: { useNullString?: boolean; legendFilter?: LegendFilterResult } = {},
  ): ArgParam[keyof ArgParam]['value'] => {
    switch (widgetVariable.type) {
      case 'fixedValue': {
        return encodeArgParam(widgetVariable.fixedValue) ?? '';
      }
      case 'globalTimePeriod': {
        if (widgetVariable.timeType === 'fromTime') {
          return encodeArgParam(globalTimePeriod.value.fromTimeUtc) ?? '';
        }
        if (widgetVariable.timeType === 'toTime') {
          return encodeArgParam(globalTimePeriod.value.toTimeUtc) ?? '';
        }
        return '';
      }
      case 'globalVariable': {
        // 범례 전체 해제 또는 이 위젯 타겟 중 selectedLegends와 매칭되는 항목이 없으면 '' 반환
        if (legendFilter?.mode === 'empty') return '';

        // NOTE: 기존에 세팅되어있던 globalVariableId가 string인 경우, 배열로 변환합니다.
        const variableId =
          typeof widgetVariable.variableId === 'string'
            ? [widgetVariable.variableId]
            : widgetVariable.variableId;

        const useGlobalVariable = standardVariables.value.filter(({ use }) => use);
        const standardTags = variableId
          .map((id) => useGlobalVariable.find(({ globalFilterId }) => `${globalFilterId}` === id))
          .filter((variable): variable is GlobalVariable => !!variable)
          .map(({ globalFilterId }) => ({
            tagKey: GLOBAL_VARIABLE_TAG_KEY,
            tagType: TAG_TYPE_KEY.GLOBAL_VARIABLE,
            tagValue: `${globalFilterId}`,
          }));

        const treeTags = (widgetVariable.treeVariableId ?? []).map((id) => ({
          tagKey: TREE_GLOBAL_VARIABLE_TAG_KEY,
          tagType: TAG_TYPE_KEY.TREE_GLOBAL_VARIABLE,
          tagValue: id,
        }));

        const allTags = [...standardTags, ...treeTags];
        if (allTags.length === 0) {
          return '';
        }
        return encodeGlobalVariableArgParam(allTags);
      }
      default: {
        return getArgParamValue(widgetVariable.selected, { useNullString }) ?? '';
      }
    }
  };

  const extractArgParamTag = (widgetVariable: WidgetVariable): ArgParam[keyof ArgParam]['tags'] => {
    switch (widgetVariable.type) {
      case 'globalVariable':
        return true;
      default:
        return undefined;
    }
  };

  const extractArgParamUserTags = (
    widgetVariable: WidgetVariable,
  ): ArgParam[keyof ArgParam]['userTags'] => {
    switch (widgetVariable.type) {
      case 'globalVariable': {
        return [
          ...widgetVariable.variableId.flatMap<UserTagRequest>((id) =>
            getRequestUserTags([id], []),
          ),
          ...(widgetVariable.treeVariableId ?? []).flatMap<UserTagRequest>((id) =>
            getRequestUserTags([], [id]),
          ),
        ];
      }
      default:
        return undefined;
    }
  };

  // 범례에서 선택된 결과만 좁혀 보낼 id 들. null 이면 narrowing 미적용(=fallback).
  // - selectedLegends === null (전체 해제) → 빈 배열 반환 → value: '' 로 직렬화
  // - selectedLegends.length > 0 → baseline 교집합 후 dedupe 한 id 들
  const resolveNarrowedTargetIds = (
    widgetVariable: WidgetVariable,
    widgetId: WidgetUUID,
    chartDataId: string,
  ): string[] | null => {
    if (widgetVariable.type !== 'globalVariable') return null;
    if (dashboardLegendStore.isDashboardLegendOpen === false) return null;
    const { selectedLegends } = dashboardLegendStore;
    if (selectedLegends === null) return [];
    if (!selectedLegends.length) return null;

    const baselineIdSet = dashboardTargetsStore.getBaselineTargetIdSet(widgetId, chartDataId);
    const seen = new Set<string>();
    const ids: string[] = [];
    selectedLegends.forEach(({ targetId }) => {
      if (targetId == null) return;
      if (!baselineIdSet.has(targetId)) return;
      if (seen.has(targetId)) return;
      seen.add(targetId);
      ids.push(targetId);
    });
    return ids;
  };

  const { getStatInfo } = useStatInfoStore();
  const { statDetailInfos } = storeToRefs(useStatDetailStore());

  const setArgumentParamValue = (
    isCommonArgument: boolean,
    statId: string,
    argumentNames: Readonly<string[]>,
    variables: ReferenceVariable[],
    widgetId: WidgetUUID,
    chartDataId: string,
  ) => {
    const { frontVersion } = getStatInfo({ statId }) || {};
    const useNullString = !!frontVersion;
    const legendFilter = resolveLegendFilter(widgetId);

    const args: ArgParam = {};
    argumentNames?.forEach((argumentName) => {
      const findValue = variables?.find(
        ({ key }) =>
          key.statId === (isCommonArgument ? CommonStatId : statId) &&
          key.argumentName === argumentName,
      )?.value;
      if (isQueryManagerStatId(statId) && toValue(options?.applyDefaultValues)) {
        args[argumentName] = {
          value: statDetailInfos.value.get(statId)?.arguments?.[argumentName] ?? '',
          tags: undefined,
        };
      } else if (findValue == null) {
        args[argumentName] = {
          value: '',
          tags: undefined,
        };
      } else {
        const narrowedIds = resolveNarrowedTargetIds(findValue, widgetId, chartDataId);
        if (narrowedIds !== null) {
          args[argumentName] = {
            value: extractArgParamValue(findValue, { useNullString, legendFilter }),
            tags: extractArgParamTag(findValue),
            targetIds: encodeArgParam(narrowedIds) ?? '',
          };
        } else {
          args[argumentName] = {
            value: extractArgParamValue(findValue, { useNullString, legendFilter }),
            tags: extractArgParamTag(findValue),
            userTags: extractArgParamUserTags(findValue),
          };
        }
      }
    });
    return args;
  };

  const getArgParams = ({
    chartType,
    chartDataIndex,
    chartDataId,
    statId,
    category,
    referenceInfo = _referenceInfo.value,
    widgetId,
  }: {
    chartType: WidgetChartType;
    chartDataIndex: number;
    chartDataId: string;
    statId: string;
    category: string;
    widgetId: WidgetUUID;
    referenceInfo?: ReferenceInfo;
  }): ArgParam[] | undefined => {
    let args: ArgParam = {};

    const commonArguments = WidgetCommonArgument[chartType];
    const commonReferenceInfo = referenceInfo?.[`${CommonChartDataIndex}`];
    if (commonArguments) {
      args = {
        ...setArgumentParamValue(
          true,
          statId,
          commonArguments,
          commonReferenceInfo ?? [],
          widgetId,
          chartDataId,
        ),
      };
    }
    const argumentNames = getArgumentList(commonArguments, statId, category);
    const referenceInfoValue = referenceInfo?.[`${chartDataIndex}`];
    args = {
      ...args,
      ...setArgumentParamValue(
        false,
        statId,
        argumentNames,
        referenceInfoValue ?? [],
        widgetId,
        chartDataId,
      ),
    };

    return [args];
  };

  return { getArgParams };
};

export const useWidgetArgParamTags = () => {
  const { getFilteredTagsPassThrough } = useRequestTags();
  const { selectedStandardVariables } = storeToRefs(useStandardVariablesStore());
  const { selectedTreeVariables } = storeToRefs(useTreeVariablesStore());

  const getArgParamTags = (argParamTags: string) => {
    const decoded = decodeGlobalVariableArgParam(argParamTags) ?? [];

    const standardIdSet = new Set(
      selectedStandardVariables.value.map(({ globalFilterId }) => `${globalFilterId}`),
    );
    const treeIdSet = new Set(
      selectedTreeVariables.value.map(({ treeVariableId }) => `${treeVariableId}`),
    );

    const targets = decoded.reduce<WidgetTarget[]>((acc, { tagType, tagValue }) => {
      if (tagType === TAG_TYPE_KEY.GLOBAL_VARIABLE && standardIdSet.has(tagValue)) {
        acc.push({
          tagKey: GLOBAL_VARIABLE_TAG_KEY,
          tagType: TAG_TYPE_KEY.GLOBAL_VARIABLE,
          tagValue,
          tagValueId: tagValue,
        });
      } else if (tagType === TAG_TYPE_KEY.TREE_GLOBAL_VARIABLE && treeIdSet.has(tagValue)) {
        acc.push({
          tagKey: GLOBAL_VARIABLE_TAG_KEY,
          tagType: TAG_TYPE_KEY.TREE_GLOBAL_VARIABLE,
          tagValue,
          tagValueId: tagValue,
        });
      }
      return acc;
    }, []);

    return getFilteredTagsPassThrough({ targets });
  };

  return { getArgParamTags };
};
